import { z } from 'zod';
import type { BaseEntity } from '@/types/entities';

/**
 * Schema de validação para updates de entidade
 * Garante que apenas campos válidos sejam atualizados
 */
export const entityUpdateSchema = z.object({
  registration_form: z.boolean().optional(),
  form: z.boolean().optional(),
  logo: z.boolean().optional(),
  observations: z.string().max(500).optional(),
  state: z.string().length(2).toUpperCase().optional(), // UF format
  region: z.string().optional(),
  instagram: z.string().optional(),
  name: z.string().max(200).optional(),
}).strict(); // strict() garante que nenhum campo extra seja aceito

/**
 * Converte nome de estado completo para UF
 * Ex: "Alagoas" -> "AL"
 */
const stateNameToUF: Record<string, string> = {
  'acre': 'AC',
  'alagoas': 'AL',
  'amapá': 'AP',
  'amazonas': 'AM',
  'bahia': 'BA',
  'ceará': 'CE',
  'distrito federal': 'DF',
  'espírito santo': 'ES',
  'goiás': 'GO',
  'maranhão': 'MA',
  'mato grosso': 'MT',
  'mato grosso do sul': 'MS',
  'minas gerais': 'MG',
  'pará': 'PA',
  'paraíba': 'PB',
  'paraná': 'PR',
  'pernambuco': 'PE',
  'piauí': 'PI',
  'rio de janeiro': 'RJ',
  'rio grande do norte': 'RN',
  'rio grande do sul': 'RS',
  'rondônia': 'RO',
  'roraima': 'RR',
  'santa catarina': 'SC',
  'são paulo': 'SP',
  'sergipe': 'SE',
  'tocantins': 'TO',
};

export function normalizeState(value: string): string {
  if (!value) return value;
  
  // Se já for UF (2 letras), retorna uppercase
  if (value.length === 2) {
    return value.toUpperCase();
  }
  
  // Tenta converter nome completo para UF
  const normalized = value.toLowerCase().trim();
  return stateNameToUF[normalized] || value;
}

/**
 * Constrói payload seguro para update de entidade
 * Remove todos os campos que não existem na tabela entities
 * Converte de camelCase (TypeScript) para snake_case (Postgres)
 */
export function buildEntityUpdatePayload(entity: Partial<BaseEntity>): Record<string, any> {
  const payload: Record<string, any> = {};
  
  // Mapeia apenas os campos permitidos e converte para snake_case
  if (entity.registrationForm !== undefined) {
    payload.registration_form = entity.registrationForm;
  }
  if (entity.form !== undefined) {
    payload.form = entity.form;
  }
  if (entity.logo !== undefined) {
    payload.logo = entity.logo;
  }
  if (entity.observations !== undefined) {
    payload.observations = entity.observations;
  }
  if (entity.state !== undefined) {
    payload.state = normalizeState(entity.state);
  }
  if (entity.region !== undefined) {
    payload.region = entity.region;
  }
  if (entity.instagram !== undefined) {
    payload.instagram = entity.instagram;
  }
  if (entity.name !== undefined) {
    payload.name = entity.name;
  }
  
  console.log('🛡️ [ENTITY_UPDATE] Payload blindado:', payload);
  console.log('🛡️ [ENTITY_UPDATE] Campos enviados:', Object.keys(payload));
  
  // Valida o payload antes de retornar
  try {
    entityUpdateSchema.parse(payload);
  } catch (error) {
    console.error('❌ [ENTITY_UPDATE] Payload inválido:', error);
    throw new Error('Dados inválidos para atualização de entidade');
  }
  
  return payload;
}
